// stahlia-frontend/src/lib/user-form.ts
// Formular-Logik Benutzerverwaltung: Rollen-Vorlagen + Validierung.
// Spiegel der Backend-Regeln aus src/modules/admin/user/routes.ts
// (createUserSchema: name min 2/max 100, password min 12; permissionsSchema-Flags).

export type RoleKey = 'admin' | 'operator' | 'lagerleiter' | 'lager'

export const ROLE_LABELS: Record<RoleKey, string> = {
  admin: 'Admin',
  operator: 'Operator',
  lagerleiter: 'Lagerleiter',
  lager: 'Lager-Mitarbeiter',
}

// ACHTUNG: DEFAULT_PERMISSIONS ist NICHT alles false (admin_login/article_general/
// order_general/order_ship/misc_print = true) — Sende-Pfade nutzen daher IMMER
// rolePermissionsComplete (alle 25 Flags explizit).
const OPERATOR_FLAGS = [
  'order_general', 'order_pay', 'order_ship',
  'article_general', 'misc_print', 'misc_reports',
] as const
const LAGER_FLAGS = ['warehouse_general', 'warehouse_book'] as const
const LAGERLEITER_FLAGS = [...LAGER_FLAGS, 'warehouse_count', 'warehouse_lead'] as const
const ADMIN_FLAGS = [
  'admin_general', 'admin_login', 'admin_design', 'admin_user_management',
  'bank_list', 'bank_check',
  'article_general', 'article_lister', 'article_purchase', 'article_purchase_prices', 'article_purchase_push',
  'order_general', 'order_pay', 'order_invoice_number', 'order_delete', 'order_finances', 'order_ship',
  'misc_print', 'misc_templates', 'misc_import_export', 'misc_reports',
  'warehouse_general', 'warehouse_book', 'warehouse_count', 'warehouse_lead', 'warehouse_admin',
] as const

export function rolePermissions(role: RoleKey): Record<string, boolean> {
  const flags: readonly string[] =
    role === 'admin' ? ADMIN_FLAGS
      : role === 'lagerleiter' ? LAGERLEITER_FLAGS
        : role === 'lager' ? LAGER_FLAGS
          : OPERATOR_FLAGS
  return Object.fromEntries(flags.map((f) => [f, true]))
}

// Vollstaendiges Flag-Set fuer Rollen-WECHSEL: alle bekannten Flags explizit,
// nicht enthaltene auf false — das Backend merged sonst mit dem Altbestand
// und ein Downgrade (Admin -> Lager) wuerde Admin-Rechte behalten.
export function rolePermissionsComplete(role: RoleKey): Record<string, boolean> {
  const all = Object.fromEntries(ADMIN_FLAGS.map((f) => [f, false]))
  return { ...all, ...rolePermissions(role) }
}

// Umkehrung fuer Rollen-Badge und Bearbeiten-Vorbelegung. Gleiche Heuristik wie
// die bisherige roleLabel()-Funktion in pages/admin/Users.tsx, plus Leiter-Stufe.
export function roleFromPermissions(p: Record<string, boolean | undefined>): RoleKey {
  if (p['admin_general']) return 'admin'
  const hatWarehouse = Object.keys(p).some((k) => k.startsWith('warehouse_') && p[k])
  const hatBetrieb = p['order_general'] || p['article_general']
  if (hatWarehouse && !hatBetrieb) {
    return p['warehouse_lead'] || p['warehouse_admin'] ? 'lagerleiter' : 'lager'
  }
  return 'operator'
}

const EMAIL_RE = /^\S+@\S+\.\S+$/

export function validateUserForm(a: {
  email: string; name: string; password: string; passwordRequired: boolean
}): string | null {
  if (!EMAIL_RE.test(a.email.trim())) return 'Ungültige E-Mail-Adresse'
  if (a.name.trim().length < 2) return 'Name muss mindestens 2 Zeichen haben'
  if (a.name.trim().length > 100) return 'Name zu lang (max. 100 Zeichen)'
  if ((a.passwordRequired || a.password.length > 0) && a.password.length < 12) {
    return 'Passwort muss mindestens 12 Zeichen haben'
  }
  return null
}
